import { CheckBadgeIcon, CheckIcon, HandThumbUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { VariableTypeIcon } from 'app/pages/Create/Courses/Course/Constants/variable';
import { EntityTypes } from 'constants/entities';
import { update } from 'core/actions/admin/data';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { IActionBasic } from 'types/domain/action';
import { IVariable, IVariableDAO, VariableType } from 'types/domain/variable';
import { getVariableOptionName } from 'utils/isVariableComplete';
import { actionEditorActions } from '../slice';
import { updateState } from 'core/actions/product/state';
import { useCallback } from 'react';
import { Modals, setModal } from 'core/slices/modals';
import { CourseVariables } from 'app/pages/Create/Courses/Course/Variables';
import { ActionVariableSelector, VariableValue, VariableIcon } from 'app/pages/Admin/Modals/Variable/action';
import { selectVariable } from 'core/selectors/admin/variable';
import { FiThumbsDown, FiThumbsUp } from 'react-icons/fi';
import { IOrgUserVariable } from 'types/domain/user';

export function ActionVariable({
  action,
  className,
  add = true,
}: {
  action: IActionBasic;
  className?: string;
  add?: boolean;
}) {
  const dispatch = useAppDispatch();
  const actionId = useAppSelector(state => state.state[EntityTypes.ACTION]);
  const actionEntityId = useAppSelector(state => state.state[EntityTypes.ACTION_ENTITY]);
  const onChange = val => dispatch(update({ type: EntityTypes.ACTION, entity: { id: action.id, ...val } }));

  const onClick = useCallback(
    e => {
      e.stopPropagation();
      if (action && action?.id !== actionId) dispatch(updateState({ type: EntityTypes.ACTION, id: action?.id }));
      if (actionEntityId) dispatch(updateState({ type: EntityTypes.ACTION_ENTITY, id: null }));

      dispatch(setModal({ type: Modals.ACTION_VARIABLE }));
    },
    [action, actionEntityId, actionId, dispatch],
  );

  return (
    <div className={`${className} flex flex-col justify-center items-center cursor-pointer p-2`}>
      {/* <CheckBadgeIcon className="w-5 h-5 mr-3" /> <span>Outcome</span> */}
      <ActionVariableSelector action={action} onChange={onChange} />
      <VariableValue
        value={action.value}
        variableOptionId={action.variableOptionId}
        variable={action.variable}
        onChange={onChange}
        className="w-full mt-4"
      />
    </div>
  );
}

export function ActionNodeVariable({ action, className }: { action: IActionBasic; className?: string }) {
  const dispatch = useAppDispatch();
  const actionId = useAppSelector(state => state.state[EntityTypes.ACTION]);
  const actionEntityId = useAppSelector(state => state.state[EntityTypes.ACTION_ENTITY]);
  const moduleVariableId = useAppSelector(
    state => state.entity.present[EntityTypes.MODULE][action.moduleId]?.variableId,
  );
  const onChange = val => dispatch(update({ type: EntityTypes.ACTION, entity: { id: action.id, ...val } }));
  const onClick = useCallback(
    e => {
      e.stopPropagation();
      if (action && action?.id !== actionId) dispatch(updateState({ type: EntityTypes.ACTION, id: action?.id }));
      if (actionEntityId) dispatch(updateState({ type: EntityTypes.ACTION_ENTITY, id: null }));

      dispatch(setModal({ type: Modals.ACTION_VARIABLE }));
    },
    [action, actionEntityId, actionId, dispatch],
  );
  const variable: IVariableDAO = useAppSelector(state => selectVariable(state, action.variableId));

  if (variable && moduleVariableId !== variable.id)
    return (
      <div className={`${className} flex items-center cursor-pointer text-xm`}>
        <div className="flex items-center">
          {<VariableIcon type={variable.type} className={'w-4 h-4 mr-2'} />}
          {<span className="font-bold">{variable.text?.shortText}</span>}
        </div>

        {/* {actionVariableNode(variable, action.value, action.variableOptionId, 'text-gray-200 ml-2')} */}
      </div>
    );
}

export function actionVariable(action, variable) {
  switch (variable.type) {
    case VariableType.BOOLEAN:
      return action.value === 1 ? <HandThumbUpIcon className="w-4 h-4 ml-5" /> : null;
    case VariableType.SCORE:
      return action.value ? (
        <span className="flex items-center ml-5">
          {action.value > 0 ? '+' : ''}
          {action.value}
        </span>
      ) : null;

    case VariableType.OPTIONS:
      return action.variableOptionId ? (
        <span className="flex items-center ml-5">{getVariableOptionName(variable, action.variableOptionId)}</span>
      ) : null;
  }
}

export function actionVariableNode(variable: IVariableDAO, orgUserVar: IOrgUserVariable, className) {
  if (!variable) return null;
  switch (variable.type) {
    case VariableType.ATTRIBUTE:
      return orgUserVar.attribute;
    case VariableType.BOOLEAN:
      return orgUserVar.value === 1 ? <HandThumbUpIcon className={className} /> : null;
    case VariableType.SCORE:
      return orgUserVar.value ? `${orgUserVar.value > 0 ? '' : ''}${orgUserVar.value}` : null;
    case VariableType.OPTIONS:
      return orgUserVar.variableOptionId != null ? (
        <span className={className}>{getVariableOptionName(variable, orgUserVar.variableOptionId)}</span>
      ) : null;
  }
}

export function actionVariableString(variable, value, variableOptionId) {
  if (!variable) return null;

  switch (variable.type) {
    case VariableType.BOOLEAN:
      return value !== null ? (value === 1 ? '✓' : 'X') : '';
    case VariableType.SCORE:
      return value !== null ? `${value > 0 ? '' : ''}${value}` : '';
    case VariableType.OPTIONS:
      return variableOptionId != null ? getVariableOptionName(variable, variableOptionId) : null;
  }
}
